import { doc, getDoc, updateDoc, type Firestore } from "firebase/firestore";
import { useEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFirestore } from "reactfire";
import { Case, EMPTY_CASE, type CaseScheduleCustomRow } from "../../models/case";
import { Client } from "../../models/client";
import { useMe } from "../../hooks/useMe";

const TAB_SLUGS = ["overview", "schedule", "documents", "tasks"] as const;
type TabSlug = (typeof TAB_SLUGS)[number];

/** Legacy URLs from earlier tabs */
const LEGACY_TAB_SLUG: Record<string, TabSlug> = {
  details: "overview",
  documents: "documents",
};

function formatSalePrice(raw: string): string {
  const n = parseInt(String(raw).replace(/\D/g, ""), 10);
  if (!Number.isFinite(n) || n <= 0) return raw?.trim() ? raw : "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function transactionLabel(t: Case["transactionType"]): string {
  const map: Record<Case["transactionType"], string> = {
    purchase: "Purchase",
    refinance: "Refinance",
    cashSale: "Cash sale",
    commercial: "Commercial",
  };
  return map[t] || t;
}

function statusForCase(c: Case): { label: string; pill: string } {
  if (c.archived) return { label: "Archived", pill: "gray" };
  if (c.complete) return { label: "Complete", pill: "green" };
  return { label: "In review", pill: "gold" };
}

function clientSecondaryLine(party: Client | undefined): string | null {
  if (!party) return null;
  if (party.email?.trim()) return party.email.trim();
  if (party.phone?.trim()) return party.phone.trim();
  return null;
}

function propertyTitle(c: Case): string {
  const a = c.address;
  const line1 = [a?.line1, a?.line2].filter(Boolean).join(", ");
  return line1 || "—";
}

function propertyCityStateZip(c: Case): string | null {
  const a = c.address;
  if (!a?.city && !a?.state && !a?.zip) return null;
  const cityState = [a.city, a.state].filter(Boolean).join(", ");
  const tail = [cityState, a.zip].filter(Boolean).join(" ");
  return tail || null;
}

function parseFirestoreDate(v: unknown): Date | null {
  if (v == null) return null;
  if (typeof v === "object" && v !== null && "toDate" in v && typeof (v as { toDate: () => Date }).toDate === "function") {
    try {
      return (v as { toDate: () => Date }).toDate();
    } catch {
      return null;
    }
  }
  if (v instanceof Date) return v;
  const d = new Date(v as string | number);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toIsoDateOnly(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function timeCreatedToIsoDate(c: Case): string {
  const d = parseFirestoreDate(c.timeCreated);
  if (!d) return "";
  return toIsoDateOnly(d);
}

function formatLongDate(iso: string): string {
  if (!iso) return "";
  const [y, m, day] = iso.split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !day) return iso;
  const d = new Date(y, m - 1, day);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

type ScheduleBuiltin = { id: string; label: string; system?: boolean };

const SCHEDULE_BUILTINS: ScheduleBuiltin[] = [
  { id: "dateReceived", label: "Date received", system: true },
  { id: "titleSearchComplete", label: "Title search complete" },
  { id: "titleCommitmentIssued", label: "Title commitment issued" },
  { id: "lenderPackageReceived", label: "Lender package received" },
  { id: "buyerReviewSent", label: "Buyer review sent" },
  { id: "finalCdReceived", label: "Final CD received" },
  { id: "settlementClosing", label: "Settlement / closing" },
  { id: "recording", label: "Recording" },
];

const IconSchedulePending = () => (
  <svg className="case-schedule-icon-pending" viewBox="0 0 18 18" fill="none" aria-hidden>
    <path
      d="M9 2.25a6.75 6.75 0 1 1-5.52 10.7"
      stroke="currentColor"
      strokeWidth="1.35"
      strokeLinecap="round"
    />
    <path
      d="M15.75 6.75 14.1 2.25 9.6 3.15"
      stroke="currentColor"
      strokeWidth="1.35"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function CaseScheduleTab({
  caseObject,
  setCaseObject,
  caseId,
  firestore,
  isAdmin,
}: {
  caseObject: Case;
  setCaseObject: Dispatch<SetStateAction<Case | null | undefined>>;
  caseId: string;
  firestore: Firestore;
  isAdmin: boolean;
}) {
  const milestones = caseObject.scheduleMilestones || {};
  const customRows = caseObject.scheduleCustomRows || [];

  const patchMilestones = (next: Record<string, string>) => {
    setCaseObject((prev) => (prev ? { ...prev, scheduleMilestones: next } : prev));
    void updateDoc(doc(firestore, "cases", caseId), { scheduleMilestones: next });
  };

  const patchCustom = (next: CaseScheduleCustomRow[]) => {
    setCaseObject((prev) => (prev ? { ...prev, scheduleCustomRows: next } : prev));
    void updateDoc(doc(firestore, "cases", caseId), { scheduleCustomRows: next });
  };

  const addCustomRow = () => {
    const row: CaseScheduleCustomRow = {
      id: `custom-${Date.now()}`,
      label: "Custom milestone",
      date: "",
    };
    patchCustom([...customRows, row]);
  };

  return (
    <div className="p-card case-detail-card">
      <div className="case-detail-card-body" style={{ paddingTop: 28 }}>
        <div className="case-schedule-card-header">
          <h2 className="case-detail-card-title" style={{ margin: 0 }}>
            Schedule
          </h2>
          <span className={`case-schedule-admin-hint${isAdmin ? " is-editable" : ""}`}>
            {isAdmin ? "ADMIN — EDITABLE" : "VIEW ONLY"}
          </span>
        </div>

        <div className="case-schedule-rows">
          {SCHEDULE_BUILTINS.map((def) => {
            const systemIso = def.system ? timeCreatedToIsoDate(caseObject) : "";
            const raw = def.system ? systemIso : milestones[def.id] || "";
            const display = raw ? formatLongDate(raw) : "";
            const pending = def.system ? !systemIso : !raw;
            const showInput = isAdmin && !def.system;

            return (
              <div key={def.id} className="case-schedule-row">
                <div className="case-schedule-icon-cell">
                  {pending ? <IconSchedulePending /> : <span className="case-schedule-icon-done" />}
                </div>
                <div className="case-schedule-label-block">
                  <span className="case-schedule-milestone-name">{def.label}</span>
                  {def.system ? <span className="case-schedule-system-tag">System</span> : null}
                </div>
                {def.system ? (
                  <div
                    className={`case-schedule-date-readonly${!display ? " is-empty" : ""}`}
                  >
                    {display || "—"}
                  </div>
                ) : showInput ? (
                  <input
                    type="date"
                    className="p-input case-schedule-date-input"
                    value={raw}
                    onChange={(e) =>
                      patchMilestones({ ...milestones, [def.id]: e.target.value })
                    }
                  />
                ) : (
                  <div
                    className={`case-schedule-date-readonly${!display ? " is-empty" : ""}`}
                  >
                    {display || "—"}
                  </div>
                )}
              </div>
            );
          })}

          {customRows.map((row) => {
            const raw = row.date || "";
            const display = raw ? formatLongDate(raw) : "";
            const pending = !raw;

            return (
              <div key={row.id} className="case-schedule-row case-schedule-row--custom">
                <div className="case-schedule-icon-cell">
                  {pending ? <IconSchedulePending /> : <span className="case-schedule-icon-done" />}
                </div>
                <div className="case-schedule-label-block" style={{ flexDirection: "column", alignItems: "stretch" }}>
                  {isAdmin ? (
                    <input
                      type="text"
                      className="p-input case-schedule-custom-label"
                      value={row.label}
                      onChange={(e) =>
                        patchCustom(
                          customRows.map((r) =>
                            r.id === row.id ? { ...r, label: e.target.value } : r
                          )
                        )
                      }
                      aria-label="Custom milestone name"
                    />
                  ) : (
                    <span className="case-schedule-milestone-name">{row.label || "Custom milestone"}</span>
                  )}
                </div>
                {isAdmin ? (
                  <input
                    type="date"
                    className="p-input case-schedule-date-input"
                    value={raw}
                    onChange={(e) =>
                      patchCustom(
                        customRows.map((r) =>
                          r.id === row.id ? { ...r, date: e.target.value } : r
                        )
                      )
                    }
                  />
                ) : (
                  <div
                    className={`case-schedule-date-readonly${!display ? " is-empty" : ""}`}
                  >
                    {display || "—"}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {isAdmin ? (
          <div className="case-schedule-add-btn">
            <button type="button" className="p-btn p-btn-ghost" onClick={addCustomRow}>
              + Add custom date
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function KvRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="case-detail-kv-row">
      <div className="case-detail-kv-label">{label}</div>
      <div className="case-detail-kv-value">{children}</div>
    </div>
  );
}

function PartyBlock({ label, line1, line2 }: { label: string; line1: string; line2?: string | null }) {
  return (
    <KvRow label={label}>
      <div style={{ fontWeight: 500, color: "var(--ink)" }}>{line1}</div>
      {line2 ? (
        <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 4 }}>{line2}</div>
      ) : null}
    </KvRow>
  );
}

function CaseDetailPage() {
  const { id, page } = useParams();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const me = useMe();
  const [caseObject, setCaseObject] = useState<Case | null | undefined>(undefined);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const snapshot = await getDoc(doc(firestore, "cases", id));
      if (cancelled) return;
      if (snapshot.exists()) {
        setCaseObject({ ...EMPTY_CASE, ...snapshot.data(), id } as Case);
      } else {
        setCaseObject(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, firestore]);

  const normalizedSlug = useMemo((): TabSlug => {
    const p = page || "";
    if (p in LEGACY_TAB_SLUG) return LEGACY_TAB_SLUG[p];
    if ((TAB_SLUGS as readonly string[]).includes(p)) return p as TabSlug;
    return "overview";
  }, [page]);

  useEffect(() => {
    if (!id || !page) return;
    const legacy = LEGACY_TAB_SLUG[page];
    if (legacy && legacy !== page) {
      navigate(`/case/${id}/${legacy}`, { replace: true });
      return;
    }
    if (!(TAB_SLUGS as readonly string[]).includes(page)) {
      navigate(`/case/${id}/overview`, { replace: true });
    }
  }, [id, page, navigate]);

  const tabIndex = TAB_SLUGS.indexOf(normalizedSlug);

  if (caseObject === undefined) {
    return (
      <div className="p-content">
        <div style={{ color: "var(--ink-soft)", padding: "48px 0" }}>Loading…</div>
      </div>
    );
  }

  if (caseObject === null) {
    return (
      <div className="p-content">
        <div className="p-breadcrumb" style={{ marginBottom: 12 }}>
          <Link to="/secure-portal" style={{ color: "inherit", textDecoration: "none" }}>
            All Cases
          </Link>
        </div>
        <h1 className="p-pagetitle">Case not found</h1>
        <p className="p-pagesub">This file may have been removed or you may not have access.</p>
      </div>
    );
  }

  const c = caseObject;
  const status = statusForCase(c);
  const cityLine = propertyCityStateZip(c);
  const buyersNames = c.buyers?.map((b) => b.name).filter(Boolean).join(" & ") || "—";
  const buyerEmails = c.buyers?.map((b) => b.email).filter(Boolean).join(", ") || null;
  const sellerNames = c.sellers?.map((s) => s.name).filter(Boolean).join(" & ") || "—";
  const sellerSecond = c.sellers?.map((s) => s.email).filter(Boolean).join(", ") || null;

  const buyersAgentLine1 = c.buyersAgent?.name?.trim() || "—";
  const buyersAgentLine2 = clientSecondaryLine(c.buyersAgent);

  const lenderLine1 = c.lender?.name?.trim() || "—";
  const lenderLine2 = clientSecondaryLine(c.lender);

  const sellersAgentLine1 = c.sellersAgent?.name?.trim() || "—";
  const sellersAgentLine2 = clientSecondaryLine(c.sellersAgent);

  return (
    <div className="p-content">
      <div className="p-breadcrumb" style={{ marginBottom: 10 }}>
        <Link to="/secure-portal" style={{ color: "inherit", textDecoration: "none" }}>
          All Cases
        </Link>
        <span style={{ opacity: 0.5, margin: "0 8px" }}>›</span>
        <span>{c.caseNumber || "—"}</span>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "10px 14px",
          marginBottom: 16,
        }}
      >
        <span style={{ fontFamily: "var(--mono)", fontSize: 13, fontWeight: 600, color: "var(--maroon)" }}>
          {c.caseNumber || "—"}
        </span>
        <span className={`p-pill ${status.pill}`}>
          <span className="dot" />
          {status.label}
        </span>
        {me?.isAdmin ? (
          <span className="p-pill lilac">
            <span className="dot" />
            Admin view
          </span>
        ) : null}
      </div>

      <h1 className="p-pagetitle" style={{ fontSize: "clamp(28px, 4vw, 40px)", marginBottom: 24 }}>
        {propertyTitle(c)}
      </h1>

      <div className="case-detail-tabs-wrap">
        <div className="p-tabs case-detail-tabs">
          {TAB_SLUGS.map((slug, idx) => (
            <div
              key={slug}
              className={`p-tab${tabIndex === idx ? " active" : ""}`}
              onClick={() => navigate(`/case/${id}/${slug}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/case/${id}/${slug}`);
                }
              }}
            >
              {slug.charAt(0).toUpperCase() + slug.slice(1)}
            </div>
          ))}
        </div>
      </div>

      {normalizedSlug === "overview" && (
        <div className="case-detail-overview-grid">
          <div className="p-card case-detail-card">
            <div className="case-detail-card-body">
              <h2 className="case-detail-card-title">Property &amp; transaction</h2>
              <div className="case-detail-kv">
                <KvRow label="File ID">{c.caseNumber || "—"}</KvRow>
                <KvRow label="Address">
                  <div style={{ fontWeight: 500 }}>{propertyTitle(c)}</div>
                  {cityLine ? (
                    <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 4 }}>{cityLine}</div>
                  ) : null}
                </KvRow>
                <KvRow label="Transaction type">{transactionLabel(c.transactionType)}</KvRow>
                <KvRow label="Sale price">
                  <span className="case-detail-sale-price">{formatSalePrice(c.salePrice)}</span>
                </KvRow>
              </div>
            </div>
          </div>

          <div className="p-card case-detail-card">
            <div className="case-detail-card-body">
              <h2 className="case-detail-card-title">Parties</h2>
              <div className="case-detail-kv">
                <PartyBlock label="Buyer" line1={buyersNames} line2={buyerEmails} />
                <PartyBlock label="Buyer's agent" line1={buyersAgentLine1} line2={buyersAgentLine2} />
                <PartyBlock label="Buyer's lender" line1={lenderLine1} line2={lenderLine2} />
                <PartyBlock label="Seller" line1={sellerNames} line2={sellerSecond} />
                <PartyBlock label="Seller's agent" line1={sellersAgentLine1} line2={sellersAgentLine2} />
              </div>
            </div>
          </div>
        </div>
      )}

      {normalizedSlug === "schedule" && id ? (
        <CaseScheduleTab
          caseObject={c}
          setCaseObject={setCaseObject}
          caseId={id}
          firestore={firestore}
          isAdmin={Boolean(me?.isAdmin)}
        />
      ) : null}

      {normalizedSlug === "documents" && (
        <div className="p-card case-detail-card">
          <div className="case-detail-card-body case-detail-placeholder">
            <p style={{ margin: 0, color: "var(--ink-soft)" }}>Documents for this file will appear here.</p>
          </div>
        </div>
      )}

      {normalizedSlug === "tasks" && (
        <div className="p-card case-detail-card">
          <div className="case-detail-card-body case-detail-placeholder">
            <p style={{ margin: 0, color: "var(--ink-soft)" }}>Tasks and checklist items will appear here.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default CaseDetailPage;
